import React from 'react'
import loadable, {
  DefaultComponent,
  LoadableClassComponent,
  LoadableComponent,
  OptionsWithoutResolver,
  OptionsWithResolver,
  Options,
} from '@loadable/component'
import pMinDelay from 'p-min-delay'
import { Spinner } from 'transfix-ui/components/Spinner'

/**
 * @note This file uses function overloads, which is a way to enable multiple
 * combinations of parameter types in TypeScript
 * @see https://www.tutorialsteacher.com/typescript/function-overloading
 */

export interface IDelayOptions {
  /**
   * Adds default minimum delay for "loading" fallback, when first loaded
   * @default 0
   */
  delay?: number
}

/**
 * Creates an async import with some defaults
 * @see https://loadable-components.com/docs/code-splitting/
 * @example
 * ```
 * const MenuAsync = loadableComponent(() => import('./Menu'), {
 *   fallback: <Spinner />,
 *   delay: 250,
 * })
 * ```
 */
function loadableComponent<Props, Module = DefaultComponent<Props>>(
  loadFn: (props: Props) => Promise<Module>,
  options: OptionsWithResolver<Props, Module> & IDelayOptions
): LoadableComponent<Props>

function loadableComponent<Props>(
  loadFn: (props: Props) => Promise<DefaultComponent<Props>>,
  options?: OptionsWithoutResolver<Props> & IDelayOptions
): LoadableComponent<Props>

function loadableComponent<Component extends React.ComponentClass<any>>(
  loadFn: (props: React.ComponentProps<Component>) => Promise<Component | { default: Component }>,
  options?: Options<React.ComponentProps<Component>, Component> & IDelayOptions
): LoadableClassComponent<Component> {
  const { delay = 0, ...restOptions } = options || {}
  return loadable(props => pMinDelay(loadFn(props), delay), {
    fallback: <Spinner />,
    ...restOptions,
  })
}

export { loadableComponent }
